/* Sprint E — Discovery Wizard / Research Readiness UX.
 *
 * Single mounted panel near the top of the Project Board. Pure
 * read-only — fetches /api/research-intel/projects/{id}/readiness
 * and renders an overall score + sub-score chips + per-stage chips
 * + recommended next actions. Action buttons surface intent text;
 * no new deep-link framework added in this sprint.
 *
 * Soft guidance only — does NOT touch any Run button or pipeline.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import {
    RefreshCw, Compass, Sparkles, ArrowRight, ChevronDown, ChevronUp,
} from "lucide-react";
import ReadinessChip from "@/components/ReadinessChip";

const SUB_SCORES = [
    { key: "website",       label: "Website" },
    { key: "evidence",      label: "Evidence" },
    { key: "competitor",    label: "Competitor" },
    { key: "persona",       label: "Persona" },
    { key: "social",        label: "Social" },
    { key: "business_plan", label: "Business Plan" },
];

const STAGE_ORDER = ["brand_dna", "research", "business_plan", "concepts", "script"];

export default function DiscoveryWizard({ projectId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(true);

    const load = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const { data } = await api.get(
                `/research-intel/projects/${projectId}/readiness`,
            );
            setData(data);
        } catch (e) {
            // Silent — wizard is best-effort; existing Run buttons
            // remain functional regardless of readiness availability.
            setData({ error: e?.userMessage || "Readiness unavailable" });
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => { load(); }, [load]);

    if (!data || data.error) {
        return (
            <div
                className="card-base p-3 mb-3 text-[11px] text-zinc-400 flex items-center justify-between"
                data-testid="discovery-wizard"
            >
                <span className="inline-flex items-center gap-2">
                    <Compass className="w-3.5 h-3.5 text-violet-300" />
                    {loading ? "Loading project readiness…" : (data?.error || "Project readiness will appear once intake exists.")}
                </span>
                <button
                    onClick={load}
                    className="text-zinc-500 hover:text-zinc-200"
                    data-testid="discovery-wizard-refresh"
                >
                    <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
                </button>
            </div>
        );
    }

    const overall = Number(data.overall || 0);
    const overallColor = overall >= 70
        ? "text-emerald-300"
        : overall >= 40
            ? "text-amber-300"
            : "text-rose-300";

    return (
        <div
            className="card-base p-4 mb-3 space-y-3"
            data-testid="discovery-wizard"
        >
            {/* Header */}
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Compass className="w-4 h-4 text-violet-300" />
                    <div className="text-sm font-semibold text-zinc-100">
                        Discovery Readiness
                    </div>
                    <span
                        className={`text-[11px] font-bold tabular-nums ${overallColor}`}
                        data-testid="discovery-wizard-overall"
                    >
                        {overall}/100
                    </span>
                </div>
                <div className="flex items-center gap-1.5">
                    <button
                        onClick={load}
                        className="text-zinc-500 hover:text-zinc-200"
                        title="Refresh"
                        data-testid="discovery-wizard-refresh"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                    </button>
                    <button
                        onClick={() => setOpen((v) => !v)}
                        className="text-zinc-500 hover:text-zinc-200"
                        title={open ? "Collapse" : "Expand"}
                        data-testid="discovery-wizard-toggle"
                    >
                        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>
                </div>
            </div>

            {open && (
                <>
                    {/* Sub-score chips */}
                    <div
                        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-1.5"
                        data-testid="discovery-wizard-scores"
                    >
                        {SUB_SCORES.map(({ key, label }) => (
                            <ReadinessChip
                                key={key}
                                label={label}
                                score={Number((data.scores || {})[key] || 0)}
                                testId={`readiness-score-${key}`}
                            />
                        ))}
                    </div>

                    {/* Per-stage statuses */}
                    <div className="space-y-1.5">
                        <div className="text-[10px] uppercase tracking-wide text-zinc-500">
                            Stage readiness — soft guidance only (Run buttons unaffected)
                        </div>
                        <div
                            className="flex flex-wrap gap-1.5"
                            data-testid="discovery-wizard-stages"
                        >
                            {STAGE_ORDER.map((sk) => {
                                const stage = (data.stages || []).find((s) => s.stage === sk);
                                if (!stage) return null;
                                return (
                                    <ReadinessChip
                                        key={sk}
                                        label={stage.label}
                                        status={stage.status}
                                        testId={`readiness-stage-${sk}`}
                                        hint={
                                            (stage.warnings || []).join(" • ")
                                            || (stage.missing_required || []).join(", ")
                                            || ""
                                        }
                                    />
                                );
                            })}
                        </div>
                    </div>

                    {/* Counts strip */}
                    <div
                        className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-1.5"
                        data-testid="discovery-wizard-counts"
                    >
                        <Count label="Approved facts"   value={data.counts?.evidence_approved} />
                        <Count label="Pending facts"    value={data.counts?.evidence_pending} />
                        <Count label="Personas"         value={data.counts?.personas} />
                        <Count label="Competitors"      value={data.counts?.competitors_confirmed} />
                        <Count label="Social profiles"  value={data.counts?.social_profiles} />
                        <Count label="Research sources" value={data.counts?.research_sources} />
                    </div>

                    {/* Recommended next actions */}
                    {(data.recommended_next_actions || []).length > 0 ? (
                        <div className="pt-2 border-t border-zinc-800 space-y-1">
                            <div className="text-[10px] uppercase tracking-wide text-zinc-500 flex items-center gap-1">
                                <Sparkles className="w-3 h-3" />
                                Next best actions
                            </div>
                            <ol
                                className="space-y-1"
                                data-testid="discovery-wizard-next-actions"
                            >
                                {data.recommended_next_actions.map((a, i) => (
                                    <li
                                        key={a.id}
                                        className="flex items-center justify-between gap-2 rounded-md border border-zinc-800 bg-zinc-950/40 px-2 py-1 text-[11px]"
                                        data-testid={`next-action-${a.id}`}
                                    >
                                        <span className="text-zinc-300">
                                            <span className="text-zinc-500 mr-2">{i + 1}.</span>
                                            {a.label}
                                        </span>
                                        <span className="text-[10px] uppercase tracking-wide text-zinc-500 inline-flex items-center gap-1">
                                            {a.target?.replaceAll("_", " ")}
                                            <ArrowRight className="w-3 h-3" />
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    ) : (
                        <div className="pt-2 border-t border-zinc-800 text-[11px] text-emerald-300 inline-flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            All clear — no required actions pending.
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function Count({ label, value }) {
    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-950/40 px-2 py-1.5">
            <div className="text-[9px] uppercase tracking-wide text-zinc-500">{label}</div>
            <div className="text-base font-semibold tabular-nums text-zinc-100">
                {Number(value || 0)}
            </div>
        </div>
    );
}
